import { z } from 'zod';
import { PolicyType } from '../../models/policy';
import { policyColumnDescriptions } from '../../models/policyColumnDescriptions';
/** Policy types the AI assistant may update or delete. Hidden system-only types are excluded. */
export const assistantPolicyTypes = Object.values(PolicyType)
    .filter(type => type !== PolicyType.ON_DEMAND_PLACEHOLDER);
/** Policy types the AI assistant may create: contact escalations have no default generator (generatePolicy) */
export const assistantCreatablePolicyTypes = assistantPolicyTypes
    .filter(type => type !== PolicyType.CONTACT_ESCALATION);
/**
 * Strict structured output forbids additionalProperties, so a policy patch is a
 * list of field/value pairs rather than a free-form object.
 */
export const assistantPolicyFieldSchema = z.object({
    field: z.string().describe(`Policy column name, from the policy field reference.`),
    value: z.union([z.string(), z.number(), z.boolean(), z.null()]),
});
export const assistantProposalOpTypes = [`create`, `update`, `delete`, `turn_safe_mode_on`];
/**
 * One flat shape for every op: OpenAI strict structured output rejects oneOf, so a
 * discriminated union is not an option. Fields that do not apply to an op are null;
 * `assistantProposalOpSchema` refines the combination.
 */
export const assistantProposalOpSchema = z.object({
    op: z.enum(assistantProposalOpTypes),
    summary: z.string().describe(`One plain-English sentence describing this change, shown to the user.`),
    policyType: z.enum(assistantCreatablePolicyTypes).nullable().describe(`Required for create. Null otherwise.`),
    policyId: z.number().int().nullable().describe(`Required for update and delete. Null otherwise.`),
    groupIds: z.array(z.number().int()).nullable().describe(`Create: groups to attach the new policy to (at least one). Update: replaces the policy's group links, null keeps them. Otherwise null.`),
    policy: z.array(assistantPolicyFieldSchema).describe(`Create: overrides on top of the Paidnice defaults. Update: fields to change. Otherwise empty.`),
}).refine(op => op.op !== `create` || (op.policyType !== null && op.groupIds !== null), {
    message: `create requires policyType and groupIds`,
}).refine(op => (op.op !== `update` && op.op !== `delete`) || op.policyId !== null, {
    message: `update and delete require policyId`,
});
export const assistantProposalSchema = z.object({
    title: z.string(),
    ops: z.array(assistantProposalOpSchema),
});
export const assistantReplyKinds = [`answer`, `clarify`, `proposal`, `email_draft`, `cannot_help`];
/** What the model drafts. Recipients are contact person ids so the model never sees email addresses */
export const assistantEmailDraftSchema = z.object({
    contactId: z.number().int().describe(`The customer the email is to, from find_contacts or find_invoices.`),
    contactPersonIds: z.array(z.string()).describe(`Contact person ids (from get_contact_detail) to send to. Empty sends to the customer's usual notification contacts.`),
    invoiceId: z.string().nullable().describe(`An invoice of this customer whose PDF is attached, when the email is about one invoice. Null for no attachment.`),
    subject: z.string(),
    body: z.string().describe(`Plain text body. No signature: the organisation's email signature is added when it is sent.`),
});
export const ASSISTANT_MAX_OPTIONS = 6;
export const ASSISTANT_MAX_QUESTIONS = 4;
export const assistantQuestionSchema = z.object({
    question: z.string().describe(`The question, short and complete on its own.`),
    options: z.array(z.string()).describe(`Short clickable answers (at most ${ASSISTANT_MAX_OPTIONS}). Empty when the answer is not one of a known set.`),
    otherOptionLabel: z.string().describe(`Label of the extra pill that lets the user answer in their own words, worded for the question (for example "A different group", "Some other customers", "Another day"). When options is empty, an invitation to type (for example "Type the amount").`),
});
export const assistantReplySchema = z.object({
    kind: z.enum(assistantReplyKinds),
    message: z.string().describe(`The reply shown to the user.`),
    proposal: assistantProposalSchema.nullable().describe(`Only set when kind is proposal.`),
    emailDraft: assistantEmailDraftSchema.nullable().describe(`Only set when kind is email_draft.`),
    questions: z.array(assistantQuestionSchema).nullable().describe(`Only when kind is clarify: every question you need answered now (at most ${ASSISTANT_MAX_QUESTIONS}), answered together by the user. Otherwise null.`),
    chatInputPlaceholder: z.string().describe(`Always set: a few words shown as the placeholder of the chat text box, telling the user what to type next. After a clarify, what an answer looks like (for example "Type a group name, a tag, or describe the customers"); after an answer or proposal, an invitation to follow up (for example "Ask a follow-up or describe another change").`),
});
export function narrowProposalOp(raw) {
    if (raw.op === `create` && raw.policyType !== null && raw.groupIds !== null) {
        return { op: `create`, summary: raw.summary, policyType: raw.policyType, groupIds: raw.groupIds, policy: raw.policy };
    }
    if (raw.op === `update` && raw.policyId !== null) {
        return { op: `update`, summary: raw.summary, policyId: raw.policyId, groupIds: raw.groupIds, policy: raw.policy };
    }
    if (raw.op === `delete` && raw.policyId !== null) {
        return { op: `delete`, summary: raw.summary, policyId: raw.policyId };
    }
    if (raw.op === `turn_safe_mode_on`) {
        return { op: `turn_safe_mode_on`, summary: raw.summary };
    }
    throw new Error(`Invalid assistant proposal op: ${raw.op}`);
}
export const ASSISTANT_MAX_PROPOSAL_OPS = 10;
/** Identity, ownership and audit columns the assistant must never set */
const DENIED_COLUMNS = new Set([
    `id`,
    `organisation_id`,
    `system`,
    `deleted`,
    `version`,
    `policy_type`,
    `created_at`,
    `updated_at`,
]);
/** Columns the assistant may set for a policy type: read by that type, minus identity columns */
export function assistantEditableColumns(type) {
    const descriptions = policyColumnDescriptions[type];
    return Object.keys(descriptions)
        .filter(column => descriptions[column].used && !DENIED_COLUMNS.has(column));
}
export function toPolicyPatch(pairs) {
    return Object.fromEntries(pairs.map(pair => [pair.field, pair.value]));
}
//# sourceMappingURL=assistantProposal.js.map